import CryptoJS from 'crypto-js';
import { v4 } from 'uuid';

import { getUploadedPhotoError } from '../validations/photos';
import { drawImageOnCanvas } from './canvas';
import { fileToArrayBuffer } from '../files';
import { escapeRegex } from '../string';
import { getSize } from '../iterable/getSize';

import { MIME_TO_EXT } from '../../constants/photos';
import { MIME_TO_EXT as VIDEO_MIME_TO_EXT } from '../../constants/video';
import { ERRORS } from '../../constants/validations';
import { TOOLS } from '../../constants/photoEditor';
import { getUploadedVideoError } from '../validations/videos';

export function setValueIn({ path, state, value }) {
  if (typeof path === 'string') {
    if (value === undefined) {
      if (state) {
        delete state[path];
      }
    } else {
      state[path] = value;
    }
  } else if (Array.isArray(path)) {
    const size = getSize(path);

    if (size === 1) {
      if (value === undefined) {
        if (state) {
          delete state[path[0]];
        }
      } else {
        state[path[0]] = value;
      }
    } else if (size > 1) {
      if (!state[path[0]]) {
        if (value === undefined) return;

        state[path[0]] = {};
      }

      setValueIn({ path: path.slice(1), state: state[path[0]], value });
    }
  }
}

export function cleanUpForTool({ current, state }) {
  switch (current) {
    case TOOLS.AI_EDIT: {
      state.aiEdit.mask.active(false);
      state.stage.mask = null;

      if (state.aiEdit.generating) {
        delete state.aiEdit.generating;
      }

      if (state.aiEdit.processing) {
        delete state.aiEdit.processing;
      }

      break;
    }

    case TOOLS.BACKGROUNDS: {
      if (state.backgrounds.processing) {
        delete state.backgrounds.processing;
      }

      if (state.backgrounds.ai.generating) {
        delete state.backgrounds.ai.generating;
      }

      if (state.backgrounds.ai.suggesting) {
        delete state.backgrounds.ai.suggesting;
      }

      if (state.backgrounds.standard.error) {
        delete state.backgrounds.standard.error;
      }

      if (state.backgrounds.standard.processing) {
        delete state.backgrounds.standard.processing;
      }

      if (state.backgrounds.standard.uploading) {
        delete state.backgrounds.standard.uploading;
      }

      break;
    }

    case TOOLS.CROP: {
      state.crop.area.active(false);
      state.stage.updatePositionAndScale({ animate: true });

      if (state.crop.expanding) {
        delete state.crop.expanding;
      }

      break;
    }

    case TOOLS.ENHANCE: {
      state.enhance.preview.active(false);
      state.stage.enhance = null;

      if (state.enhance.processing) {
        delete state.enhance.processing;
      }

      if (state.enhance.progress) {
        delete state.enhance.progress;
      }

      break;
    }

    case TOOLS.IMAGE_TO_VIDEO: {
      state.stage.video = null;

      if (state.imageToVideo.ai.generated) {
        state.imageToVideo.video.active(false);
      }

      break;
    }

    case TOOLS.LAYERS: {
      state.layers.foreground.active(false);
      break;
    }

    default: {
      break;
    }
  }
}

export function setUpForTool({ current, state }) {
  switch (current) {
    case TOOLS.AI_EDIT: {
      state.stage.mask = state.aiEdit.mask;
      break;
    }

    case TOOLS.BACKGROUNDS: {
      break;
    }

    case TOOLS.CROP: {
      state.crop.area.active(true);
      state.stage.updatePositionAndScale({ animate: true });
      break;
    }

    case TOOLS.ENHANCE: {
      state.stage.enhance = state.enhance.preview;

      if (state.enhance.enhanced) {
        state.enhance.preview.active(true);
      }

      break;
    }

    case TOOLS.IMAGE_TO_VIDEO: {
      state.stage.video = state.imageToVideo.video;

      if (state.imageToVideo.ai.generated) {
        state.imageToVideo.video.active(true);
      }

      break;
    }

    case TOOLS.LAYERS: {
      state.layers.foreground.active(true);
      break;
    }

    default: {
      break;
    }
  }
}

export function renderStateOnCanvas(state) {
  const filters = state.image.filters();
  const { x, y } = state.crop.area.position();
  const { width, height } = state.crop.area.size();
  const options = { filters };
  const config = { x, y, width, height };

  function onImage(image) {
    if (state.crop.preset?.dimensions) {
      const { dimensions } = state.crop.preset;

      return Promise.resolve(drawImageOnCanvas(image, 0, 0, width, height, 0, 0, dimensions.width, dimensions.height));
    }

    return Promise.resolve(drawImageOnCanvas(image, 0, 0, width, height));
  }

  return state.stage.toImage(options, config).then(onImage);
}

export function getHashFromBuffer(buffer) {
  return CryptoJS.SHA1(CryptoJS.lib.WordArray.create(buffer)).toString();
}

export function getPhotoFromState(state) {
  function onCanvas(canvas) {
    function onBlob(blob) {
      function onArrayBuffer(data) {
        return Promise.resolve({
          format: MIME_TO_EXT[state.mime],
          data: data,
          hash: getHashFromBuffer(data),
          mime: state.mime,
          url: URL.createObjectURL(blob),
        });
      }

      return fileToArrayBuffer(blob).then(onArrayBuffer);
    }

    return canvas.getBlob(state.mime).then(onBlob);
  }

  return renderStateOnCanvas(state).then(onCanvas);
}

export function shapePhotoFileForEditor({ channel, file }) {
  return new Promise(async function executor(resolve, reject) {
    try {
      await getUploadedPhotoError({ channel, file });
      const { name, type } = file;
      const format = MIME_TO_EXT[type];

      if (!format) {
        reject(ERRORS.PHOTOS.UNSUPPORTED_FORMAT[channel]);
        return;
      }

      const data = await fileToArrayBuffer(file);

      resolve({
        blob: file,
        data,
        format,
        hash: getHashFromBuffer(data),
        id: v4(),
        mime: type,
        name,
        url: URL.createObjectURL(file),
      });
    } catch (error) {
      reject(error);
    }
  });
}

export function shapeVideoFileForEditor({ channel, file }) {
  return new Promise(async function executor(resolve, reject) {
    try {
      await getUploadedVideoError({ channel, file });
      const { name, type } = file;
      const format = VIDEO_MIME_TO_EXT[type];

      if (!format) {
        reject(ERRORS.VIDEOS.UNSUPPORTED_FORMAT[channel]);
        return;
      }

      const data = await fileToArrayBuffer(file);

      resolve({
        blob: file,
        data,
        format,
        hash: getHashFromBuffer(data),
        id: v4(),
        mime: type,
        name,
        url: URL.createObjectURL(file),
      });
    } catch (error) {
      reject(error);
    }
  });
}

export function shapeUnsplashPhoto({ id, links, slug, urls }) {
  const { download_location: downloadLocation } = links;
  const { small: url } = urls;
  const name = slug.match(new RegExp(`.*(?=-${escapeRegex(id)})`))[0];

  return { downloadLocation, id, name, url };
}

export function getMediaLibraryFilters({ page, ...rest }) {
  return {
    limit: 6,
    offset: (page || 0) * 6,
    order_by: 'created_at',
    order_type: false,
    ...rest,
  };
}

export function shapeLibraryPhoto(image) {
  const { url, uuid: id } = image;
  return { id, url };
}
